import {
  CFormCheck,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { EyeIcon } from "@heroicons/react/24/solid";
import React, { useCallback, useEffect, useState } from "react";
import DeleteIcon from "../../../components/icons/DeleteIcon";
import PromptModal from "../../../components/modals/PromptModal";

import { DELETE_ACTIONS } from "../../../config/constants";
import { colors } from "../../../config/theme";
import {
  Chip,
  Dash,
  formatDateString,
  IconAction,
} from "../../../helpers/general";
import { tdStyle, thStyle } from "../../../helpers/constants";

interface TaxiCouponsTableProps {
  coupons: Coupon[];
  TaxiCouponsTableToBeDeleted: (action: BulkDeleteActions) => void;
  deleteCoupon: (id: string) => void;
  isDeleting: boolean;
}

interface RowProps {
  coupon: Coupon;
  deletedList: React.Dispatch<BulkDeleteActions>;
  onDelete: () => void;
  isDeleting: boolean;
}

const COLUMNS = [
  { key: "name", label: "Coupon" },
  { key: "expiration", label: "Expiration" },
  { key: "status", label: "Statut" },
  { key: "usage", label: "Utilisation" },
  { key: "actions", label: "Actions", align: "right" },
] as const;

function CouponRow({ coupon, deletedList, onDelete, isDeleting }: RowProps) {
  const [isChecked, setIsChecked] = useState(false);

  const toggle = useCallback(
    (checked: boolean) => {
      setIsChecked(checked);
      deletedList({
        type: checked ? DELETE_ACTIONS.ADD : DELETE_ACTIONS.REMOVE,
        id: coupon.id,
      });
    },
    [deletedList, coupon.id],
  );

  // A row that unmounts (page change) must not stay in the selection
  useEffect(
    () => () => deletedList({ type: DELETE_ACTIONS.REMOVE, id: coupon.id }),
    [deletedList, coupon.id],
  );

  const expired =
    Boolean(coupon.experationDate) &&
    new Date(coupon.experationDate) < new Date();

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      {/* Name + comment */}
      <CTableDataCell style={tdStyle}>
        <div
          className="d-flex align-items-center gap-3"
          style={{ minWidth: 220 }}
        >
          <CFormCheck
            checked={isChecked}
            onChange={(e) => toggle(e.target.checked)}
          />
          <div style={{ minWidth: 0 }}>
            <div className="fw-semibold">{coupon.name}</div>
            {coupon.comments ? (
              <div
                className="text-muted text-truncate"
                style={{ fontSize: 12, maxWidth: 260 }}
                title={coupon.comments}
              >
                {coupon.comments}
              </div>
            ) : null}
          </div>
        </div>
      </CTableDataCell>

      {/* Expiration */}
      <CTableDataCell style={tdStyle}>
        {coupon.experationDate ? (
          <div className="d-flex flex-column align-items-start gap-1">
            <span style={{ whiteSpace: "nowrap" }}>
              {formatDateString(coupon.experationDate)}
            </span>
            {expired && <Chip tone="danger">Expiré</Chip>}
          </div>
        ) : (
          <Dash />
        )}
      </CTableDataCell>

      {/* Status */}
      <CTableDataCell style={tdStyle}>
        <div className="d-flex flex-wrap gap-1">
          <Chip tone={coupon.isActive ? "success" : "danger"}>
            {coupon.isActive ? "Actif" : "Inactif"}
          </Chip>
          <Chip>{coupon.isPublic ? "Public" : "Privé"}</Chip>
        </div>
      </CTableDataCell>

      {/* Usage limits */}
      <CTableDataCell style={tdStyle}>
        <div style={{ whiteSpace: "nowrap" }}>
          <span className="fw-semibold">{coupon.maxUsage ?? "∞"}</span>
          <span className="text-muted"> au total</span>
        </div>
        <div
          className="text-muted"
          style={{ fontSize: 12, whiteSpace: "nowrap" }}
        >
          {coupon.maxPerUser ?? "∞"} par personne
        </div>
      </CTableDataCell>

      {/* Actions */}
      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          <IconAction title="Voir" to={`/taxi/discounts/${coupon.id}`}>
            <EyeIcon width="16" height="16" fill={colors.secondary} />
          </IconAction>
          <IconAction
            title="Supprimer"
            danger
            onClick={onDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <CSpinner size="sm" />
            ) : (
              <DeleteIcon width="18" height="18" fill={colors.primary} />
            )}
          </IconAction>
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

export default function TaxiCouponsTable({
  coupons,
  TaxiCouponsTableToBeDeleted,
  deleteCoupon,
  isDeleting,
}: TaxiCouponsTableProps) {
  const [couponToDelete, setCouponToDelete] = useState<Coupon | null>(null);

  // Close the confirmation once the delete request finishes
  useEffect(() => {
    if (!isDeleting) setCouponToDelete(null);
  }, [isDeleting]);

  return (
    <div>
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            {COLUMNS.map((col) => (
              <CTableHeaderCell
                key={col.key}
                scope="col"
                style={
                  "align" in col
                    ? { ...thStyle, textAlign: col.align }
                    : thStyle
                }
              >
                {col.label}
              </CTableHeaderCell>
            ))}
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {coupons?.map((coupon) => (
            <CouponRow
              key={coupon.id}
              coupon={coupon}
              deletedList={TaxiCouponsTableToBeDeleted}
              onDelete={() => setCouponToDelete(coupon)}
              isDeleting={isDeleting && couponToDelete?.id === coupon.id}
            />
          ))}
        </CTableBody>
      </CTable>

      <PromptModal
        open={couponToDelete !== null}
        onAccept={() => couponToDelete && deleteCoupon(couponToDelete.id)}
        onClose={() => setCouponToDelete(null)}
        loading={isDeleting}
      />
    </div>
  );
}
